/**
 * FieldDoc's site header: the default composition (monogram + name + intent
 * menu + mobile sidebar) plus an always-visible language switcher, because a
 * farmer arriving in Marathi must be able to see how to stay in Marathi
 * without asking for it in English first.
 *
 * The switcher sends the request as an ordinary visitor turn through
 * `useSendIntent`, which is the sanctioned path: the agent answers in that
 * language and the platform commits the session's presentation locale, so the
 * choice sticks for this browser. Entries are always written in their own
 * script and never translated — a farmer looking for "मराठी" must find the
 * word "मराठी".
 */
import { useEffect, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { Languages, Menu, X } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import { messages as platformMessages } from '@/app/lib/localization/messages.ts';
import { useLocalization } from '@/app/lib/localization/LocalizationProvider.tsx';
import { useSendIntent } from '@/app/lib/a2ui/blocks/use-send-intent.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { NavItem } from '@/app/lib/stage/nav-model.ts';
import { cn } from '@/app/lib/utils';

const headerMessages = defineMessages({
  languageLabel: {
    id: 'header.language.groupLabel',
    defaultMessage: 'Language',
    description:
      'Accessible name for the header control that switches the language of the conversation.',
  },
});

/** The three languages the clinic actually serves, each in its own script, with
 *  the request written as the farmer would say it in that language. */
const LANGUAGES: ReadonlyArray<{ tag: string; selfName: string; request: string }> = [
  {
    tag: 'mr',
    selfName: 'मराठी',
    request: 'मराठीत बोला',
  },
  {
    tag: 'hi',
    selfName: 'हिंदी',
    request: 'हिंदी में बात करें',
  },
  {
    tag: 'en',
    selfName: 'English',
    request: 'Speak to me in English',
  },
];

function activeLanguageTag(messageLocale: string): string {
  try {
    return new Intl.Locale(messageLocale).language;
  } catch {
    return 'en';
  }
}

const LanguageSwitcher: FC<{ layout: 'bar' | 'sidebar' }> = ({ layout }) => {
  const intl = useIntl();
  const { messageLocale } = useLocalization();
  const sendIntent = useSendIntent();
  const active = activeLanguageTag(messageLocale);

  return (
    <div
      role="group"
      aria-label={intl.formatMessage(headerMessages.languageLabel)}
      className={cn(
        'flex items-center gap-0.5 rounded-md border border-border bg-muted/60 p-0.5',
        layout === 'sidebar' && 'w-full',
      )}
    >
      {LANGUAGES.map((language) => {
        const isActive = language.tag === active;
        return (
          <button
            key={language.tag}
            type="button"
            lang={language.tag}
            aria-pressed={isActive}
            onClick={() => sendIntent(language.request)}
            style={{ touchAction: 'manipulation' }}
            className={cn(
              'cursor-pointer whitespace-nowrap rounded-sm border-none px-2.5 py-1.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              layout === 'sidebar' && 'flex-1 py-2.5 text-center',
              isActive
                ? 'bg-card text-primary shadow-sm'
                : 'bg-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {/* Self-names always in full: an abbreviation a farmer has to
                decode defeats the point of a visible switcher. */}
            {language.selfName}
          </button>
        );
      })}
    </div>
  );
};

export const FieldDocHeader: FC<AgentHeaderProps> = ({ brandName, navItems, onNavigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleItemClick = (item: NavItem) => {
    setMenuOpen(false);
    onNavigate(item);
  };

  return (
    <header className="flex-none border-b border-border/70 bg-background/80 px-5 pt-[env(safe-area-inset-top)] backdrop-blur-sm md:px-20">
      <div className="mx-auto flex w-full max-w-container-content items-center gap-x-3 py-3 sm:gap-x-6 sm:py-4">
        <div className="flex min-w-0 flex-none items-center gap-2.5 sm:gap-3">
          <div className="grid h-7 w-7 flex-none place-items-center rounded-sm bg-primary font-display text-base font-semibold text-primary-foreground sm:h-8 sm:w-8 sm:text-lg">
            {brandName.charAt(0).toUpperCase()}
          </div>
          {/* The brand name yields to the language switcher on a phone: the
              monogram still carries the identity, and the switcher must stay
              visible at 360px. */}
          <div
            className="hidden whitespace-nowrap font-display text-lg font-semibold tracking-tight text-foreground sm:block sm:text-xl"
            translate="no"
          >
            {brandName}
          </div>
        </div>

        <nav
          className="scrollbar-none hidden min-w-0 items-center gap-1 overflow-x-auto lg:ml-auto lg:flex"
          aria-label={intl.formatMessage(platformMessages.siteNavigation)}
        >
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              className="flex-none cursor-pointer touch-manipulation whitespace-nowrap rounded-sm border-none bg-transparent px-3 py-2 text-xs font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-muted data-[active=true]:text-primary"
              data-active={item.active === true}
              onClick={() => onNavigate(item)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Always on screen, phone included — this is how a farmer finds their
            own language, so it does not hide behind the hamburger. */}
        <div className={cn('flex-none', navItems.length > 0 ? 'ml-auto lg:ml-0' : 'ml-auto')}>
          <LanguageSwitcher layout="bar" />
        </div>

        {navItems.length > 0 && (
          <button
            type="button"
            className="grid h-9 w-9 flex-none cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary lg:hidden"
            aria-label={intl.formatMessage(platformMessages.openMenu)}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
      </div>

      {createPortal(
        <>
          <div
            aria-hidden="true"
            className={cn(
              'fixed inset-0 z-[11] bg-foreground/25 transition-opacity duration-[220ms] lg:hidden',
              menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            onPointerDown={() => setMenuOpen(false)}
          />
          <div
            className={cn(
              'fixed inset-y-0 right-0 z-[12] flex w-72 flex-col border-l border-border bg-background shadow-lift transition-transform duration-[220ms] ease-in-out lg:hidden',
              menuOpen ? 'translate-x-0' : 'translate-x-full',
            )}
          >
            <div className="flex items-center justify-between py-3 pl-5 pr-3">
              <div
                className="font-display text-base font-semibold tracking-tight text-foreground"
                translate="no"
              >
                {brandName}
              </div>
              <button
                type="button"
                className="grid h-9 w-9 cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                aria-label={intl.formatMessage(platformMessages.closeMenu)}
                onClick={() => setMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav
              className="flex flex-col gap-1 px-3 pb-4"
              aria-label={intl.formatMessage(platformMessages.siteNavigation)}
            >
              {navItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="cursor-pointer touch-manipulation rounded-sm border-none bg-transparent px-3 py-3 text-left text-xs font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-muted data-[active=true]:text-primary"
                  data-active={item.active === true}
                  tabIndex={menuOpen ? 0 : -1}
                  onClick={() => handleItemClick(item)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="mt-auto flex items-center gap-2 border-t border-border px-5 py-4">
              <Languages className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <LanguageSwitcher layout="sidebar" />
            </div>
          </div>
        </>,
        document.body,
      )}
    </header>
  );
};
